import { forwardRef, type ComponentProps } from 'react';
import { AsYouType } from 'libphonenumber-js';
import { Input } from '@sara/ui/components/input';

/**
 * Live-format US phone input as the user types, e.g. `5551234567` → `(555) 123-4567`.
 * Strips a leading country code and caps at 10 national digits. The formatted
 * value is what the field stores; normalizeUsPhone() (schemas.ts) tolerates the
 * formatting when converting to E.164 for the API.
 */
export function formatUsPhoneInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return new AsYouType('US').input(local.slice(0, 10));
}

interface PhoneInputProps extends Omit<
  ComponentProps<typeof Input>,
  'onChange' | 'value' | 'type'
> {
  value: string;
  onChange: (value: string) => void;
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, ...props },
  ref
) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    const prevDigits = value.replace(/\D/g, '');
    const nextDigits = next.replace(/\D/g, '');
    // Backspace can land on a mask character (e.g. the ")" in "(555)"), which
    // leaves the digits unchanged — the formatter would just re-add it, trapping
    // the user. Detect that (raw text shrank, digits didn't) and drop a real digit.
    const digits =
      next.length < value.length && nextDigits === prevDigits
        ? nextDigits.slice(0, -1)
        : nextDigits;
    onChange(formatUsPhoneInput(digits));
  };

  return (
    <Input {...props} ref={ref} type="tel" inputMode="tel" value={value} onChange={handleChange} />
  );
});
